import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { ThemeService } from 'src/app/services/theme.service';
import { CardsService, ThemeColors } from 'src/app/services/cards.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-theme-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, ColorPickerModule, MatButtonModule],
  templateUrl: './theme-picker.component.html',
  styleUrls: ['./theme-picker.component.scss']
})
export class ThemePickerComponent implements OnInit {
  @Input() cardId!: string;
  @Output() themeSelected = new EventEmitter<{ startColor: string; endColor: string }>();

  startColor = '#7b2ff7';
  endColor = '#f107a3';
  selectedColorType: 'start' | 'end' = 'start';
  currentColor = this.startColor;
  textColor = '#fff';
  isSaving = false;

  constructor(
    private dialogRef: MatDialogRef<ThemePickerComponent>,
    private themeService: ThemeService,
    private cardsService: CardsService
  ) {}

  ngOnInit() {
  console.log('🧠 ThemePickerComponent initialized');

  if (this.cardId) {
    console.log('[ThemePicker] cardId:', this.cardId);

    const theme: ThemeColors | null = this.cardsService.getCardThemeFromState(this.cardId);
    console.log('[ThemePicker] fetched theme:', theme);

    if (theme) {
      this.startColor = theme.primary || this.startColor;
      this.endColor = theme.accent || this.endColor;
      console.log('[ThemePicker] applied colors -> start:', this.startColor, ', end:', this.endColor);
    } else {
      console.warn('[ThemePicker] No theme found for card:', this.cardId);
    }

    this.updateCurrentColor();
    this.updateTextColor();
  } else {
    console.warn('[ThemePicker] No cardId provided!');
  }
}


  selectColor(type: 'start' | 'end') {
    this.selectedColorType = type;
    this.updateCurrentColor();
  }

  private updateCurrentColor() {
    this.currentColor = this.selectedColorType === 'start' ? this.startColor : this.endColor;
  }

  onColorChange(value: string) {
    if (this.selectedColorType === 'start') this.startColor = value;
    else this.endColor = value;
    this.updateTextColor();
  }

  /** Update text color based on gradient */
  updateTextColor() {
    const avgColor = this.averageHex(this.startColor, this.endColor);
    this.textColor = this.isDark(avgColor) ? '#fff' : '#000';
  }

  hexToRgb(hex: string) {
    hex = hex.replace('#', '');
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
  }

  isDark(hex: string) {
    const { r, g, b } = this.hexToRgb(hex);
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance < 140;
  }

  averageHex(hex1: string, hex2: string) {
    const c1 = this.hexToRgb(hex1);
    const c2 = this.hexToRgb(hex2);
    const r = Math.round((c1.r + c2.r) / 2);
    const g = Math.round((c1.g + c2.g) / 2);
    const b = Math.round((c1.b + c2.b) / 2);
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }

  apply() {
  if (!this.cardId) return;

  this.isSaving = true;

  this.themeService.updateTheme(this.cardId, {
    primary_color_1: this.startColor,
    primary_color_2: this.endColor
  })
  .pipe(finalize(() => this.isSaving = false))
  .subscribe({
    next: async (res) => {
      if (res.success) {
        this.themeSelected.emit({ startColor: this.startColor, endColor: this.endColor });

        try {
          // Refresh the card from DB to sync state
          await this.cardsService.getCardById(this.cardId, true);
          console.log('🔄 Card data refreshed after theme update');
        } catch (error) {
          console.error('❌ Failed to refresh card after theme update:', error);
        }
      }

      this.dialogRef.close();
    },
    error: () => this.dialogRef.close()
  });
}


  cancel() {
    this.dialogRef.close();
  }
}
